import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import type { AudienceMeta } from '@shared/constants/audiences';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getIcon } from '../../lib/lucide-icons';
import { getAudienceTheme } from '../../config/audience-theme';
import type { AudienceVoice } from '../../config/audience-voice';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

export interface SectorCTAProps {
  audience: AudienceMeta;
  voice: AudienceVoice;
  /** ID HTML — utile pour ancres internes (ex: #devenir-partenaire) */
  id?: string;
}

/**
 * SectorCTA — Section finale de conversion adaptée à l'audience.
 *
 * Design 2026 :
 *  - Fond noir profond avec halo coloré contextuel
 *  - Badge audience + titre fort + paragraphe contextualisé
 *  - 2 CTAs (primaire + secondaire ghost outline)
 *  - Footnote (engagement, NDA, transparence)
 */
export function SectorCTA({ audience, voice, id }: SectorCTAProps) {
  const theme = getAudienceTheme(audience.id);
  const PrimaryIcon = getIcon(voice.ctaFinal.primary.iconName);

  return (
    <section
      id={id}
      className="relative overflow-hidden bg-ink py-16 text-ivory-50 md:py-20"
    >
      {/* Halo coloré contextuel */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          className={cn(
            'absolute -top-32 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full blur-[110px]',
            theme.ctaFinalHalo,
          )}
        />
        {audience.id === 'artisans' && (
          <div className="absolute -bottom-32 left-0 h-[400px] w-[400px] rounded-full bg-brand-500/30 blur-[100px]" />
        )}
      </div>

      {/* Grid pattern subtil */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage:
            'radial-gradient(ellipse 80% 60% at 50% 50%, black 0%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 60% at 50% 50%, black 0%, transparent 80%)',
        }}
      />

      <Container className="relative">
        <Reveal>
          <div className="mx-auto max-w-3xl text-center">
            {voice.ctaFinal.badge && (
              <Badge
                variant="default"
                size="md"
                className={cn(
                  'mx-auto inline-flex',
                  audience.id === 'artisans'
                    ? 'bg-accent-500/20 text-accent-200 ring-accent-400/30'
                    : audience.tone === 'lavender' || audience.tone === 'lavender-700'
                      ? 'bg-lavender-500/20 text-lavender-200 ring-lavender-400/30'
                      : audience.tone === 'sage'
                        ? 'bg-sage-300/20 text-sage-200 ring-sage-400/30'
                        : 'bg-brand-500/20 text-brand-200 ring-brand-400/30',
                )}
              >
                {voice.ctaFinal.primary.iconName && (
                  <PrimaryIcon className="h-3 w-3" strokeWidth={2} />
                )}
                {voice.ctaFinal.badge}
              </Badge>
            )}

            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.05, ease: easeStripe }}
              className="mt-5 font-display text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold leading-[1.05] tracking-[-0.025em] text-ivory-50 text-balance"
            >
              {voice.ctaFinal.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.12, ease: easeStripe }}
              className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-stone-300"
            >
              {voice.ctaFinal.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.2, ease: easeStripe }}
              className="mt-7 flex flex-wrap items-center justify-center gap-2.5"
            >
              <Button asChild size="lg" variant={theme.primaryButtonVariant} className="group">
                <a href={voice.ctaFinal.primary.href}>
                  {voice.ctaFinal.primary.label}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                </a>
              </Button>
              <Button asChild size="lg" variant="dark-outline">
                <a href={voice.ctaFinal.secondary.href}>
                  {voice.ctaFinal.secondary.label}
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.2} />
                </a>
              </Button>
            </motion.div>

            {voice.ctaFinal.footnote && (
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: 0.32, ease: easeStripe }}
                className="mt-6 inline-flex items-center justify-center gap-1.5 text-xs text-stone-400"
              >
                <CheckCircle2 className="h-3 w-3" />
                {voice.ctaFinal.footnote}
              </motion.p>
            )}
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
